import React, { useCallback } from "react";

import { Redirect, Switch, useHistory, useRouteMatch } from "react-router-dom";

import { FeatureRoute } from "components/Authorization/FeatureRoute";
import { Sundries } from "components/BillingWorkFlow/Sundries";
import { Column, MainContentWrapper } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import TabRow from "components/TabSelector/TabRow";

import { BillingScreens } from "constants/billing";
import { ApiModel } from "constants/loading";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { fontSizes, space } from "constants/theme";

interface BillingSubTab {
  id: string;
  title: string;
  Content: React.ComponentType;
}

const subTabs: BillingSubTab[] = [
  {
    id: BillingScreens.SUNDRIES,
    title: "Sundries",
    Content: Sundries,
  },
];

// TabRow's tab interface uses `component` for a custom tab visual; passing the
// content component as a tab field would render it inside the tab strip. Strip
// the Content field for TabRow and keep it for the Switch below.
const tabRowItems = subTabs.map(({ id, title }) => ({ id, title }));

// Match the compact spacing used by the Sale Reports sub-tabs.
const tabRowStyle = {
  fontSize: fontSizes.alpha,
  margin: 0,
  padding: `${space[2]}px ${space[1]}px 0 ${space[1]}px`,
};

export const MobileBillingWorkflow: React.FC = () => {
  const match = useRouteMatch();
  const subRouteMatch = useRouteMatch<{ section?: string }>(
    `${match.path}/:section`,
  );
  const selectedTab = subRouteMatch?.params.section || subTabs[0].id;
  const history = useHistory();

  const setSelectedTab = useCallback(
    (id: string) => {
      history.push(`${match.url}/${id}`);
    },
    [history, match.url],
  );

  return (
    <WaitForSync
      requiredData={[
        ApiModel.MANUAL_ADJUSTMENTS,
        ApiModel.BUSINESSES,
        ApiModel.EMAIL,
      ]}
    >
      <Column full>
        <MainContentWrapper>
          <TabRow
            tabs={tabRowItems}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            style={tabRowStyle}
          />
        </MainContentWrapper>

        <Switch>
          {subTabs.map(({ id, Content }) => (
            <FeatureRoute
              key={id}
              roleDeploymentPermissions={[
                DeploymentPermission.featureManualCharges,
              ]}
              saleyardPermissions={[SaleyardPermission.featureManualCharges]}
              path={`${match.path}/${id}`}
            >
              <Content />
            </FeatureRoute>
          ))}
          {/* Catch any sub-route that didn't match a FeatureRoute above —
              including desktop-only entry points like /billing/overview that
              mobile doesn't expose — and send the user to the first available
              sub-tab. */}
          <Redirect to={`${match.url}/${subTabs[0].id}`} />
        </Switch>
      </Column>
    </WaitForSync>
  );
};
